import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Gauge,
  Fuel,
  Settings2,
  Calendar,
  Car,
  ChevronLeft,
  ChevronRight,
  Info,
  Facebook,
  Instagram,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Mapping Multiavisos keys to Spanish friendly labels
const TRANSLATION_MAP = {
  Doors: 'Puertas',
  Color: 'Color',
  BodyType: 'Tipo de carrocería',
  Direction: 'Dirección',
  Motor: 'Motor',
  Traction: 'Tracción',
  Cylinders: 'Cilindros',
  Valves: 'Válvulas',
  ExteriorSpecs: 'Exterior',
  InteriorSpecs: 'Interior',
  ComfortSpecs: 'Confort',
  SecuritySpecs: 'Seguridad',
  ClimatizationSpecs: 'Climatización',
  ConnectivitySpecs: 'Conectividad',
  MaintenanceSpecs: 'Mantenimiento',
  SoundSpecs: 'Sonido',
  BatteryType: 'Tipo de batería',
  BateryCapacity: 'Capacidad batería',
  Range: 'Autonomía',
  FastChargingTime: 'Tiempo de carga rápida',
  SlowChargingTime: 'Tiempo de carga lenta',
  Power: 'Potencia máxima',
  AnnualRoadTax: 'Patente anual',
  BimonthlyRoadTax: 'Patente cuota',
  DeliveryTime: 'Entrega',
  Steering: 'Dirección'
};

const VehiculoDetalle = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/multiavisos/vehiculos/${id}`);
        setVehicle(response.data);
      } catch (err) {
        console.error('Error fetching vehicle', err);
        setError('No se pudo cargar la información del vehículo.');
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Cargando detalles...</div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <div className="text-xl text-red-500">{error || 'Vehículo no encontrado'}</div>
        <Link to="/vehiculos" className="px-4 py-2 bg-cyan-500 text-black rounded-lg font-bold hover:bg-cyan-400 transition">
          Volver al catálogo
        </Link>
      </div>
    );
  }

  // Helper functions
  const proxyImageUrl = (url) => {
    return `${process.env.REACT_APP_BACKEND_URL}/api/multiavisos/images?url=${encodeURIComponent(url)}`;
  };

  const images = vehicle.Images && vehicle.Images.length > 0 ? vehicle.Images : [];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Helper to extract non-empty flat specifications
  const basicSpecs = [
    { key: 'Doors', value: vehicle.Doors },
    { key: 'Color', value: vehicle.Color },
    { key: 'BodyType', value: vehicle.BodyType },
    { key: 'Direction', value: vehicle.Direction },
    { key: 'Steering', value: vehicle.Steering },
    { key: 'Motor', value: vehicle.Motor },
    { key: 'Traction', value: vehicle.Traction },
    { key: 'Cylinders', value: vehicle.Cylinders },
    { key: 'Valves', value: vehicle.Valves },
    { key: 'BatteryType', value: vehicle.BatteryType },
    { key: 'BateryCapacity', value: vehicle.BateryCapacity?.Display },
    { key: 'Range', value: vehicle.Range?.Display },
    { key: 'FastChargingTime', value: vehicle.FastChargingTime?.Display },
    { key: 'SlowChargingTime', value: vehicle.SlowChargingTime?.Display },
    { key: 'Power', value: vehicle.Power?.Display },
    { key: 'AnnualRoadTax', value: vehicle.AnnualRoadTax?.Display },
    { key: 'BimonthlyRoadTax', value: vehicle.BimonthlyRoadTax?.Display },
    { key: 'DeliveryTime', value: vehicle.DeliveryTime }
  ].filter(spec => spec.value && spec.value.toString().trim() !== '');

  // Helper to extract non-empty list specifications
  const featureListSpecs = [
    { key: 'ExteriorSpecs', value: vehicle.ExteriorSpecs },
    { key: 'InteriorSpecs', value: vehicle.InteriorSpecs },
    { key: 'ComfortSpecs', value: vehicle.ComfortSpecs },
    { key: 'SecuritySpecs', value: vehicle.SecuritySpecs },
    { key: 'ClimatizationSpecs', value: vehicle.ClimatizationSpecs },
    { key: 'ConnectivitySpecs', value: vehicle.ConnectivitySpecs },
    { key: 'MaintenanceSpecs', value: vehicle.MaintenanceSpecs },
    { key: 'SoundSpecs', value: vehicle.SoundSpecs }
  ].filter(spec => spec.value && Array.isArray(spec.value) && spec.value.length > 0);

  return (
    <div className="min-h-screen bg-black text-white selection:bg-cyan-500/30">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/vehiculos" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold text-sm sm:text-base">Volver</span>
            </Link>
            <h1 className="text-lg font-bold truncate mx-4 hidden sm:block">
              {vehicle.Brand} {vehicle.Model} {vehicle.Version}
            </h1>
            <div className="w-20"></div> {/* Spacer for center alignment */}
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left Column - Image Slider */}
            <div className="space-y-4">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-white/5 border border-white/10 group">
                {images.length > 0 ? (
                  <>
                    <motion.img
                      key={currentImageIndex}
                      src={proxyImageUrl(images[currentImageIndex])}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="w-full h-full object-cover"
                      alt={`Foto ${currentImageIndex + 1} de ${vehicle.Brand} ${vehicle.Model}`}
                    />

                    {images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/80 backdrop-blur opacity-0 group-hover:opacity-100 transition-all border border-white/10"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/80 backdrop-blur opacity-0 group-hover:opacity-100 transition-all border border-white/10"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>

                        {/* Image Counter Badge */}
                        <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur px-3 py-1 rounded-lg text-xs font-medium border border-white/10">
                          {currentImageIndex + 1} / {images.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white/50">
                    <Car className="w-16 h-16 mb-2 opacity-50" />
                    <span>Sin imagen disponible</span>
                  </div>
                )}

                {/* Featured Badge */}
                {(vehicle.Featured === "true" || vehicle.Featured === true) && (
                  <div className="absolute top-4 left-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 shadow-lg shadow-cyan-500/30">
                      <svg className="w-3.5 h-3.5 text-black" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-xs font-bold text-black uppercase tracking-wide">Premium</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="relative group/thumbs mt-4">
                  {/* Left Scroll Button (Desktop Only) */}
                  <button
                    onClick={() => {
                      const container = document.getElementById('thumbnails-container');
                      if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
                    }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-8 h-8 rounded-full bg-black/80 text-white flex items-center justify-center border border-white/20 z-10 opacity-0 group-hover/thumbs:opacity-100 hidden md:flex transition-opacity hover:bg-black hover:scale-110"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <div
                    id="thumbnails-container"
                    className="flex gap-2 overflow-x-auto pb-2 min-h-24 scroll-smooth"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    <style dangerouslySetInnerHTML={{
                      __html: `
                      #thumbnails-container::-webkit-scrollbar { display: none; }
                      @media (max-width: 768px) {
                        #thumbnails-container::-webkit-scrollbar { display: block; height: 4px; }
                        #thumbnails-container::-webkit-scrollbar-track { background: rgba(255,255,255,0.1); border-radius: 4px; }
                        #thumbnails-container::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 4px; }
                      }
                    `}} />
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`relative flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 transition-all ${currentImageIndex === idx ? 'border-cyan-500' : 'border-transparent opacity-60 hover:opacity-100'}`}
                      >
                        <img src={proxyImageUrl(img)} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>

                  {/* Right Scroll Button (Desktop Only) */}
                  <button
                    onClick={() => {
                      const container = document.getElementById('thumbnails-container');
                      if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
                    }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-8 h-8 rounded-full bg-black/80 text-white flex items-center justify-center border border-white/20 z-10 opacity-0 group-hover/thumbs:opacity-100 hidden md:flex transition-opacity hover:bg-black hover:scale-110"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
              {/* Comments / Description */}
              {(vehicle.Description || vehicle.Comments) && (vehicle.Description?.trim() !== '' || vehicle.Comments?.trim() !== '') && (
                <div className="prose prose-invert max-w-none">
                  <h3 className="text-xl font-bold flex items-center gap-2 mb-4">
                    <Info className="w-5 h-5 text-cyan-500" />
                    Descripción
                  </h3>
                  <div className="bg-white/10 p-6 rounded-2xl border border-white/10 text-white/80 leading-relaxed whitespace-pre-wrap">
                    {vehicle.Description && vehicle.Description.trim() !== '' ? (
                      <div dangerouslySetInnerHTML={{ __html: vehicle.Description }} />
                    ) : (
                      vehicle.Comments
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="space-y-8">
              {/* Title & Main Stats */}
              <div>
                <h2 className="text-4xl font-extrabold text-white mb-2">
                  {vehicle.Brand} <span className="text-white/80 font-medium">{vehicle.Model}</span>
                </h2>
                {vehicle.Version && (
                  <p className="text-xl text-cyan-400 mb-6 font-medium">{vehicle.Version}</p>
                )}

                <div className="flex flex-wrap gap-4 mb-6">
                  <Badge variant="outline" className="border-white/20 bg-white/5 text-base py-1.5 px-4 rounded-xl">
                    <Calendar className="w-4 h-4 mr-2 text-white/50" />
                    {vehicle.Year}
                  </Badge>
                  <Badge variant="outline" className="border-white/20 bg-white/5 text-base py-1.5 px-4 rounded-xl">
                    <Gauge className="w-4 h-4 mr-2 text-white/50" />
                    {vehicle.Mileage?.Value > 0 ? `${(vehicle.Mileage.Value).toLocaleString()} km` : '0 km'}
                  </Badge>
                  <Badge variant="outline" className="border-white/20 bg-white/5 text-base py-1.5 px-4 rounded-xl">
                    <Fuel className="w-4 h-4 mr-2 text-white/50" />
                    {vehicle.Fuel || 'N/A'}
                  </Badge>
                  <Badge variant="outline" className="border-white/20 bg-white/5 text-base py-1.5 px-4 rounded-xl">
                    <Settings2 className="w-4 h-4 mr-2 text-white/50" />
                    {vehicle.Transmission || 'N/A'}
                  </Badge>
                </div>

                <div className="p-6 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
                  <span className="text-white/50 text-sm uppercase font-bold tracking-wider mb-1 block">Precio Venta</span>
                  <div className="text-5xl font-black text-white flex items-center gap-2">
                    <span className="text-cyan-500 font-medium text-3xl">{vehicle.Price?.Currency === 'USD' ? 'U$S' : '$'}</span>
                    {vehicle.Price?.Value ? vehicle.Price.Value.toLocaleString() : 'Consultar'}
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-4">
                    <a href="#" className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-all hover:shadow-lg hover:shadow-cyan-500/20">
                      <Phone className="w-5 h-5" />
                      Contactar
                    </a>
                    <a href="#" className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-all border border-white/10">
                      Agendar Visita
                    </a>
                  </div>
                </div>
              </div>



              {/* Basic Details Grid */}
              {basicSpecs.length > 0 && (
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2 mb-4 border-b border-white/10 pb-2">
                    Especificaciones
                  </h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    {basicSpecs.map(spec => (
                      <div key={spec.key} className="flex justify-between border-b border-white/5 pb-2">
                        <span className="text-white/50">{TRANSLATION_MAP[spec.key] || spec.key}</span>
                        <span className="font-medium text-right">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Feature Lists */}
              {featureListSpecs.length > 0 && (
                <div className="space-y-6 pt-6">
                  {featureListSpecs.map(specList => (
                    <div key={specList.key}>
                      <h4 className="text-lg font-bold mb-3 text-cyan-400 border-b border-white/10 pb-2">
                        {TRANSLATION_MAP[specList.key] || specList.key}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {specList.value.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                            <span className="text-cyan-500 mt-1">•</span>
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        </div>
      </main>

      {/* Footer (Simplified version for detail page) */}
      <footer className="bg-black border-t border-white/10 mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center sm:text-left flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <span className="font-bold text-lg mr-2">Amaya Motors</span>
            <span className="text-white/40 text-sm">© 2024. Todos los derechos reservados.</span>
          </div>
          <div className="flex gap-4">
            <a href="https://www.facebook.com/profile.php?id=61584072839314#" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="https://www.instagram.com/amayabyd/" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VehiculoDetalle;
